import React, { useState, useEffect, useMemo } from 'react';
import { Link, useParams } from "react-router-dom";
import { IoWalletSharp } from "react-icons/io5";
import { HiDotsVertical } from "react-icons/hi";
import { TbZoomMoney } from "react-icons/tb";
import { GiWallet } from "react-icons/gi";
import updatestocksApi from '../data/stockhistory';
import { Pie, Line } from 'react-chartjs-2';
import {
    Chart as ChartJS,
    ArcElement,
    Tooltip,
    Legend,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement
} from 'chart.js';
import parties from '../data/party';
import stocksApi from '../data/stock';

ChartJS.register(ArcElement, Tooltip, Legend, CategoryScale, LinearScale, PointElement, LineElement);

interface Party {
    id: number;
    date: string;
    name: string;
    number: string;
    contact_person_name: string;
    contact_person_number: string;
    categories: string[];
    deleteparty: number;
    amount?: number;
    percentage?: number;
    total_amount?: number;
}

interface StockCategory {
    id: number;
    stock_name: string;
    unit: string;
    total_amount: string | number;
    indirect_charges: string | number;
    perUnitCharge: string | number;
    category: string;
}
interface Stock {
    id: number | string;
    date?: string;
    party_id: number;
    total_amount: number | string;
    indirect_charges?: number | string;
    categories: StockCategory[];
    name?: string;
    date_of_purchase?: string;
    unit?: string;
    price_per_unit?: string | number;
}

const growthOptions = {
    plugins: { legend: { display: false }, tooltip: { enabled: true } },
    scales: {
        x: { grid: { display: false }, ticks: { color: '#b1b5c4' } },
        y: { grid: { color: "#f3f3f3" }, ticks: { color: '#b1b5c4' } }
    },
    maintainAspectRatio: false,
    elements: { point: { radius: 5 } }
};
const pieOptions = { plugins: { legend: { display: false } } };

const PERIOD_OPTIONS = [
    { key: "All", label: "All" },
    { key: "today", label: "Today" },
    { key: "thisWeek", label: "This Week" },
    { key: "thisMonth", label: "This Month" }
];

function getPeriodRange(period: string): [Date | null, Date | null] {
    const now = new Date();
    let from: Date | null = null, to: Date | null = null;
    switch (period) {
        case "today":
            from = new Date(now); from.setHours(0, 0, 0, 0);
            to = new Date(now); to.setHours(23, 59, 59, 999);
            break;
        case "thisWeek":
            from = new Date(now); from.setDate(now.getDate() - now.getDay());
            from.setHours(0, 0, 0, 0);
            to = new Date(now); to.setHours(23, 59, 59, 999);
            break;
        case "thisMonth":
            from = new Date(now.getFullYear(), now.getMonth(), 1, 0, 0, 0, 0);
            to = new Date(now); to.setHours(23, 59, 59, 999);
            break;
        case "All":
        default:
            from = null; to = null;
    }
    return [from, to];
}
// Small utils
function filterForSingleParty(stocks: Stock[], partyId?: number | string) {
    if (!partyId) return [];
    return stocks.filter(s => s.party_id === Number(partyId));
}
function getAllUniqueCategories(stocks: Stock[]): string[] {
    const set = new Set<string>();
    for (const stock of stocks) {
        const cats = Array.isArray(stock.categories) ? stock.categories : [];
        cats.forEach(cat => {
            if (cat && typeof cat === "object" && typeof cat.category === "string" && cat.category.trim()) {
                set.add(cat.category);
            }
        });
    }
    return Array.from(set);
}
function filterStocksByCategoryAndPeriod(stocks: Stock[], category: string, period: string) {
    const [from, to] = getPeriodRange(period);
    let filtered = stocks;
    if (category && category !== 'All') {
        filtered = filtered.filter(stock => {
            const cats = Array.isArray(stock.categories) ? stock.categories : [];
            return cats.some((cat: any) => typeof cat.category === "string" && cat.category === category);
        });
    }
    if (from && to) {
        filtered = filtered.filter(stock => {
            const stockDateStr = stock.date_of_purchase || stock.date;
            if (!stockDateStr) return false;
            const stockDate = new Date(stockDateStr);
            return stockDate >= from && stockDate <= to;
        });
    }
    return filtered;
}
type HasDate = {
    date?: string;
    date_of_purchase?: string;
};
function filterStocksByDateRange<T extends HasDate>(
    items: T[],
    fromDate: string | null,
    toDate: string | null
): T[] {
    if (!fromDate && !toDate) return items;
    return items.filter(item => {
        const dateStr = item.date_of_purchase || item.date;
        if (!dateStr) return false;
        const itemDate = new Date(dateStr);
        let fromCondition = true, toCondition = true;
        if (fromDate) fromCondition = itemDate >= new Date(fromDate);
        if (toDate) {
            const toD = new Date(toDate);
            toD.setHours(23, 59, 59, 999);
            toCondition = itemDate <= toD;
        }
        return fromCondition && toCondition;
    });
}
function allStocksMinTransactionDate(stocks: Stock[]) {
    if (!stocks || stocks.length === 0) return undefined;
    const dates = stocks.map(s => s.date_of_purchase || s.date).map(d => (d ? new Date(d) : undefined)).filter(Boolean) as Date[];
    if (!dates.length) return undefined;
    return dates.reduce((min, d) => d < min ? d : min, dates[0]);
}
function allStocksMaxTransactionDate(stocks: Stock[]) {
    if (!stocks || stocks.length === 0) return undefined;
    const dates = stocks.map(s => s.date_of_purchase || s.date).map(d => (d ? new Date(d) : undefined)).filter(Boolean) as Date[];
    if (!dates.length) return undefined;
    return dates.reduce((max, d) => d > max ? d : max, dates[0]);
}
function calculateOpeningBalanceSingleParty(
    stocks: Stock[],
    fromDate: string | null,
    toDate: string | null,
    allParties: any[],
    partyId: Number | null
): number {
    if (!fromDate && !toDate) {
        // Sum total_amount only for the current party from allParties
        let sum = 0;
        for (const p of allParties) {
            // console.log("p.id ", p.id, " party id ", partyId);
            if (p.id === partyId) {
                
                let amt = p.total_amount;
                
                sum += amt;
            }
        }
        return sum;
    }
    let sum = 0;
    for (const stock of stocks) {
        const dateStr = stock.date_of_purchase || stock.date;
        if (!dateStr) continue;
        const d = new Date(dateStr);
        if (isNaN(d.getTime())) continue;
        let include = true;
        if (fromDate) {
            const from = new Date(fromDate);
            if (d < from) include = false;
        }
        if (toDate) {
            const to = new Date(toDate);
            to.setHours(23, 59, 59, 999);
            if (d > to) include = false;
        }
        if (include) {
            const cats = Array.isArray(stock.categories) ? stock.categories : [];
            for (const cat of cats) {
                let amt = cat.total_amount;
                if (typeof amt === "string") amt = parseFloat(amt) || 0;
                sum += amt || 0;
            }
        }
    }
    return sum;
}
function calculateTotalInvested(stocks: Stock[]): number {
    let sum = 0;
    for (const stock of stocks) {
        const cats = Array.isArray(stock.categories) ? stock.categories : [];
        for (const cat of cats) {
            let amt = cat.total_amount;
            if (typeof amt === "string") amt = parseFloat(amt) || 0;
            sum += amt || 0;
        }
    }
    return sum;
}
function calculateSimpleInterest(principal: number, rate: number, time: number) {
    return principal * rate * time / 100;
}
function calculateCompoundInterest(principal: number, rate: number, time: number) {
    return principal * (Math.pow(1 + rate / 100, time) - 1);
}
function findLatestStockHistory(
    updateStockRecords: any[],
    stock_id: number,
    cat_id?: number
) {
    const filtered = updateStockRecords.filter(
        rec =>
            rec.stock_id === stock_id &&
            String(rec.cat_id ?? "") === String(cat_id ?? "")
    );
    if (filtered.length === 0) return null;
    return filtered.reduce((latest, current) => {
        const latestTime = latest.current_price_date
            ? new Date(latest.current_price_date).getTime()
            : 0;
        const currentTime = current.current_price_date
            ? new Date(current.current_price_date).getTime()
            : 0;
        if (currentTime > latestTime) return current;
        if (currentTime === latestTime) {
            return (current.id ?? 0) > (latest.id ?? 0) ? current : latest;
        }
        return latest;
    }, filtered[0]);
}
async function updatestocksApiSettings(stocks: Stock[]): Promise<number> {
    try {
        const updateStockRecords = await updatestocksApi();
        let sum = 0;
        for (const stock of stocks) {
            const categories = Array.isArray(stock.categories) ? stock.categories : [];
            for (const cat of categories) {
                const stockId = Number(stock.id);
                const catId = Number(cat.id);
                const latest = findLatestStockHistory(updateStockRecords, stockId, catId);
                if (latest && latest.current_price) {
                    let curPrice = latest.current_price;
                    if (typeof curPrice === "string") curPrice = parseFloat(curPrice) || 0;
                    sum += curPrice || 0;
                }
            }
        }
        return sum;
    } catch (err) {
        return 0;
    }
}
async function fetchSettings() {
    try {
        const res = await fetch(`${process.env.REACT_APP_API_URL}/auth/setting`);
        if (!res.ok) return [];
        const data = await res.json();
        return data;
    } catch (err) {
        return [];
    }
}

function SinglePartyPage() {
    const { id } = useParams<{ id: string }>();
    const partyId = useMemo(() => Number(id), [id]);

    const [allParties, setAllParties] = useState<Party[]>([]);
    const [allStocks, setAllStocks] = useState<Stock[]>([]);
    const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
    // UI Filters etc
    const [dateRange, setDateRange] = useState<{ from: string | null, to: string | null }>({ from: null, to: null });
    const [growthPeriod, setGrowthPeriod] = useState<string>('All');
    const [growthCategory, setGrowthCategory] = useState<string>('All');
    const [piePeriod, setPiePeriod] = useState<string>('All');
    const [pieCategory, setPieCategory] = useState<string>('All');
    const [uniqueCategories, setUniqueCategories] = useState<string[]>([]);
    // Balances
    const [openingBalance, setOpeningBalance] = useState<number>(0);
    const [currentBalance, setCurrentBalance] = useState<number>(0);
    const [totalEarning, setTotalEarning] = useState<number>(0);
    // Party invested & party return percent for showing below Current Balance
    const [partyInvested, setPartyInvested] = useState<number>(0);
    const [partyReturnPercent, setPartyReturnPercent] = useState<string>('0%');
    // Settings
    const [settings, setSettings] = useState<any[]>([]);
    // Chart
    const [growthChartData, setGrowthChartData] = useState<any>({
        labels: [],
        datasets: [{
            label: "Working Balance",
            data: [],
            fill: false,
            backgroundColor: "#34d399",
            borderColor: "#34d399",
            borderWidth: 2,
            pointBackgroundColor: "#fff",
            pointBorderColor: "#34d399",
            tension: 0.4
        }]
    });
    const [pieData, setPieData] = useState<any>({
        labels: [],
        datasets: [{
            data: [],
            backgroundColor: ["#1294F2", "#F6830F", "#00CA4E", "#F83C3F", "#FF6B9D", "#9B59B6", "#E67E22", "#3498DB"],
            borderWidth: 0,
        }]
    });
    const [pieCategoryDetails, setPieCategoryDetails] = useState<Array<any>>([]);
    // Interest
    const [useBaseCalculation, setUseBaseCalculation] = useState(false);
    const [interestStartDate, setInterestStartDate] = useState<string>('');
    const [interestEndDate, setInterestEndDate] = useState<string>('');
    const [interestSummary, setInterestSummary] = useState<{
        type: string,
        rate: number,
        calculated: number,
        start: string,
        end: string,
        time: number
    } | null>(null);

    // Fetch all parties and stocks
    useEffect(() => {
        (async () => {
            setSettings(await fetchSettings());
        })();
    }, []);

    useEffect(() => {
        async function loadData() {
            const partiesRes = await parties();
            setAllParties(Array.isArray(partiesRes) ? partiesRes : []);
            const stocksData = await stocksApi();
            const cleaned = Array.isArray(stocksData)
                ? stocksData.map((s: any) => ({
                    ...s,
                    categories:
                        typeof s.categories === "string"
                            ? (() => {
                                try {
                                    const parsed = JSON.parse(s.categories);
                                    return Array.isArray(parsed) ? parsed : [parsed];
                                } catch {
                                    return [s.categories];
                                }
                            })()
                            : Array.isArray(s.categories)
                                ? s.categories
                                : [],
                })) : [];
            setAllStocks(cleaned);
            // categories for this party
            const myStocks = filterForSingleParty(cleaned, partyId);
            setUniqueCategories(['All', ...getAllUniqueCategories(myStocks)]);
        }
        loadData();
    }, [partyId]);

    // Current party & party's stocks
    const partyIdNum = partyId ? Number(partyId) : undefined;
    const partyStocks = filterForSingleParty(allStocks, partyIdNum);

    function dashboardMinTransactionDateString() {
        const dt = allStocksMinTransactionDate(partyStocks);
        return dt ? dt.toISOString().substring(0, 10) : "";
    }
    function dashboardMaxTransactionDateString() {
        const dt = allStocksMaxTransactionDate(partyStocks);
        return dt ? dt.toISOString().substring(0, 10) : "";
    }
    // Opening, current, earning
    useEffect(() => {
        async function update() {
            const stocksFiltered =
                (!dateRange.from && !dateRange.to)
                    ? partyStocks
                    : filterStocksByDateRange(partyStocks, dateRange.from, dateRange.to);
            setOpeningBalance(calculateOpeningBalanceSingleParty(partyStocks, dateRange.from, dateRange.to, allParties, partyId));
            const curBal = await updatestocksApiSettings(stocksFiltered);
            setCurrentBalance(curBal);
            setTotalEarning(curBal - calculateTotalInvested(stocksFiltered));
            // Calculate party invested and party return percent for this party
            let invested = 0;
            for (const stock of stocksFiltered) {
                const cats = Array.isArray(stock.categories) ? stock.categories : [];
                for (const cat of cats) {
                    let amt = cat.total_amount;
                    if (typeof amt === "string") amt = parseFloat(amt) || 0;
                    invested += amt || 0;
                }
            }
            setPartyInvested(invested);
            // Show percent return based on invested
            if (invested > 0) {
                const ret = ((curBal - invested) / invested) * 100;
                setPartyReturnPercent(`${ret >= 0 ? '+' : ''}${ret.toFixed(2)}%`);
            } else {
                setPartyReturnPercent('0%');
            }
        }
        update();
        // eslint-disable-next-line
    }, [allStocks, dateRange, partyId]);

    // Interest
    useEffect(() => {
        if (!useBaseCalculation) {
            setInterestSummary(null);
            return;
        }
        let pType = 'compound';
        let percent = 0;
        if (settings && settings.length > 0) {
            const t = settings.find((s: any) => s.meta_key === "interest_type");
            const p = settings.find((s: any) => s.meta_key === "percentage");
            if (t) pType = t.meta_value;
            if (p) percent = parseFloat(p.meta_value || "0");
        }
        let principal = totalEarning;
        let psMinDt = allStocksMinTransactionDate(partyStocks);
        let psMaxDt = allStocksMaxTransactionDate(partyStocks);
        let startDate = interestStartDate || (psMinDt ? psMinDt.toISOString().substring(0, 10) : "");
        let endDate = interestEndDate || (psMaxDt ? psMaxDt.toISOString().substring(0, 10) : "");
        let months = 1;
        if (startDate && endDate) {
            const sd = new Date(startDate);
            const ed = new Date(endDate);
            const yearsDiff = ed.getFullYear() - sd.getFullYear();
            const monthsDiff = ed.getMonth() - sd.getMonth();
            months = yearsDiff * 12 + monthsDiff + (ed.getDate() >= sd.getDate() ? 1 : 0);
            if (months < 1) months = 1;
        }
        const timeInYears = months / 12;
        let calculated = 0;
        if (pType === 'simple') {
            calculated = calculateSimpleInterest(principal, percent, timeInYears);
        } else {
            calculated = calculateCompoundInterest(principal, percent, timeInYears);
        }
        setInterestSummary({
            type: pType,
            rate: percent,
            calculated,
            start: startDate,
            end: endDate,
            time: months
        });
        // eslint-disable-next-line
    }, [useBaseCalculation, interestStartDate, interestEndDate, settings, allStocks, totalEarning, partyIdNum]);

    // Growth Chart
    useEffect(() => {
        let filteredStocks = (!dateRange.from && !dateRange.to) ? partyStocks : filterStocksByDateRange(partyStocks, dateRange.from, dateRange.to);
        filteredStocks = filterStocksByCategoryAndPeriod(filteredStocks, growthCategory, growthPeriod);
        const sorted = [...filteredStocks].sort((a, b) => {
            const dateA = a.date_of_purchase || a.date || '';
            const dateB = b.date_of_purchase || b.date || '';
            const timeA = dateA ? new Date(dateA).getTime() : 0;
            const timeB = dateB ? new Date(dateB).getTime() : 0;
            return timeA - timeB;
        });
        const dateBalanceMap = new Map<string, number>();
        const dateLabelMap = new Map<string, string>();
        sorted.forEach((stock) => {
            const categories = Array.isArray(stock.categories) ? stock.categories : [];
            let stockTotal = 0;
            categories.forEach((cat: any) => {
                if (growthCategory === "All" || cat.category === growthCategory) {
                    const amt = (typeof cat.total_amount === 'string' ? parseFloat(cat.total_amount) : cat.total_amount) || 0;
                    const ic = (typeof cat.indirect_charges === 'string' ? parseFloat(cat.indirect_charges) : cat.indirect_charges) || 0;
                    stockTotal += amt + ic;
                }
            });
            const dt = stock.date_of_purchase || stock.date || new Date().toISOString().split('T')[0];
            if (dt) {
                const obj = new Date(dt);
                const key = obj.toISOString().split('T')[0];
                const label = obj.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
                if (!dateBalanceMap.has(key)) {
                    dateBalanceMap.set(key, 0);
                    dateLabelMap.set(key, label);
                }
                dateBalanceMap.set(key, (dateBalanceMap.get(key) || 0) + stockTotal);
            }
        });
        const sortedDateKeys = Array.from(dateBalanceMap.keys()).sort((a, b) => new Date(a).getTime() - new Date(b).getTime());
        const sortedDateLabels = sortedDateKeys.map(key => dateLabelMap.get(key) || key);
        const balanceData: number[] = [];
        let runBal = 0;
        sortedDateKeys.forEach(dateKey => {
            runBal += dateBalanceMap.get(dateKey) || 0;
            balanceData.push(runBal);
        });
        setGrowthChartData({
            labels: sortedDateLabels.length > 0 ? sortedDateLabels : sorted.map((_, i) => `Transaction ${i + 1}`),
            datasets: [{
                label: "Working Balance",
                data: balanceData.length > 0 ? balanceData : [],
                fill: false,
                backgroundColor: "#34d399",
                borderColor: "#34d399",
                borderWidth: 2,
                pointBackgroundColor: "#fff",
                pointBorderColor: "#34d399",
                tension: 0.4
            }]
        });
    }, [allStocks, growthPeriod, growthCategory, dateRange, partyId]);

    // Pie Chart
    useEffect(() => {
        let filteredStocks = (!dateRange.from && !dateRange.to) ? partyStocks : filterStocksByDateRange(partyStocks, dateRange.from, dateRange.to);
        filteredStocks = filterStocksByCategoryAndPeriod(filteredStocks, pieCategory, piePeriod);
        let categoryMap: { [key: string]: number } = {};
        let categoryStockBreakdown: { [key: string]: any[] } = {};
        filteredStocks.forEach((stock) => {
            const categories = Array.isArray(stock.categories) ? stock.categories : [];
            categories.forEach((cat: any) => {
                if (pieCategory === "All" || cat.category === pieCategory) {
                    const catName = cat.category || 'Other';
                    const amt = (typeof cat.total_amount === 'string' ? parseFloat(cat.total_amount) : cat.total_amount) || 0;
                    const ic = (typeof cat.indirect_charges === 'string' ? parseFloat(cat.indirect_charges) : cat.indirect_charges) || 0;
                    const sum = amt + ic;
                    if (!categoryMap[catName]) {
                        categoryMap[catName] = 0;
                        categoryStockBreakdown[catName] = [];
                    }
                    categoryMap[catName] += sum;
                    categoryStockBreakdown[catName].push({
                        stock_name: cat.stock_name,
                        unit: cat.unit,
                        total_amount: amt,
                        indirect_charges: ic,
                        perUnitCharge: typeof cat.perUnitCharge === 'string' ? parseFloat(cat.perUnitCharge) || 0 : cat.perUnitCharge || 0
                    });
                }
            });
        });
        const cEntries = Object.entries(categoryMap);
        const pieLabels = cEntries.map(([name]) => name);
        const pieValues = cEntries.map(([, amt]) => amt);
        const pieColors = ["#1294F2", "#F6830F", "#00CA4E", "#F83C3F", "#FF6B9D", "#9B59B6", "#E67E22", "#3498DB"];
        const totalCat = pieValues.reduce((s, a) => s + a, 0);
        setPieCategoryDetails(
            cEntries.map(([name, amt], i) => ({
                name,
                amount: amt,
                percentage: totalCat > 0 ? amt / totalCat * 100 : 0,
                color: pieColors[i % pieColors.length],
                stockBreakdown: categoryStockBreakdown[name]
            }))
        );
        setPieData({
            labels: pieLabels,
            datasets: [{
                data: pieValues,
                backgroundColor: pieColors.slice(0, pieLabels.length),
                borderWidth: 0,
            }]
        });
    }, [allStocks, pieCategory, piePeriod, uniqueCategories, dateRange, partyId]);

    // Party obj for header
    const thisParty = allParties.find(p => p.id === partyIdNum);

    return (
        <div className="bg-[#F6F6F6] min-h-screen ">
            <div className='flex flex-wrap w-full'>
                <div className="flex w-full">
                    <div
                        className={`p-6 transition-all duration-300 ${sidebarCollapsed
                            ? "w-[calc(100%-64px)]"
                            : "lg:w-[75%] w-[100%]"}`
                        }
                        style={
                            sidebarCollapsed
                                ? { width: "calc(100% - 64px)", transition: "width 0.3s" }
                                : {}
                        }
                    >
                        <div className="flex flex-col sm:flex-row justify-start items-start sm:items-center gap-2 mb-4">
                            <div className="flex items-center gap-2">
                                <label className="text-[#3F4765] text-[15px] font-[500]">Date Range:</label>
                                <input
                                    type="date"
                                    className="border border-[#E4E8F0] px-2 py-1 rounded text-xs focus:outline-none"
                                    value={dateRange.from || ""}
                                    min={dashboardMinTransactionDateString()}
                                    max={dashboardMaxTransactionDateString()}
                                    onChange={e =>
                                        setDateRange(prev => ({
                                            ...prev,
                                            from: e.target.value || null
                                        }))
                                    }
                                />
                                <span className="text-[#8C8C8C] text-[12px]">to</span>
                                <input
                                    type="date"
                                    className="border border-[#E4E8F0] px-2 py-1 rounded text-xs focus:outline-none"
                                    value={dateRange.to || ""}
                                    min={dateRange.from || dashboardMinTransactionDateString()}
                                    max={dashboardMaxTransactionDateString()}
                                    onChange={e =>
                                        setDateRange(prev => ({
                                            ...prev,
                                            to: e.target.value || null
                                        }))
                                    }
                                />
                                {(dateRange.from || dateRange.to) && (
                                    <button
                                        className="ml-2 text-[#3176D3] text-xs underline"
                                        onClick={() => setDateRange({ from: null, to: null })}
                                        title="Reset date range"
                                    >
                                        Reset
                                    </button>
                                )}
                            </div>
                        </div>
                        <div className="flex items-center justify-between w-[100%] mb-4">
                            <div>
                                <p className="text-[#3F4765] text-[24px] font-[600]">
                                    {thisParty?.name ? thisParty.name : "Party"} Details
                                </p>
                            </div>
                        </div>
                        <div className='flex flex-wrap justify-between gap-[8px] w-full'>
                            <div className='lg:w-[32%] md:w-[48%] w-[100%] bg-white border-[1px] border-[#DDDDDD] rounded-[14px] p-3'>
                                <div className='w-full flex justify-between items-center mb-3'>
                                    <div className='bg-[#FFF2E5] rounded-[50%] p-2'>
                                        <IoWalletSharp className='text-[#F49A40]' />
                                    </div>
                                    <HiDotsVertical />
                                </div>
                                <span className='text-[#9FA8C7] text-[14px]'>Opening Balance</span>
                                <div className='flex justify-between items-center'>
                                    <p className='text-[#25282C] text-[25px] font-[600]'>₹{openingBalance.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
                                    <div className='bg-[#E5FFF1] w-[75px] rounded-[40px] flex justify-center items-center py-1'>
                                        <span className='text-[#00AE4E] text-[12px]'>
                                            {openingBalance > 0 && currentBalance > 0
                                                ? `${((currentBalance - openingBalance) / openingBalance * 100).toFixed(2)}%`
                                                : '0%'}
                                        </span>
                                    </div>
                                </div>
                            </div>
                            <div className='lg:w-[32%] md:w-[48%] w-[100%] bg-white border-[1px] border-[#DDDDDD] rounded-[14px] p-3'>
                                <div className='w-full flex justify-between items-center mb-3'>
                                    <div className='bg-[#FFDFF6] rounded-[50%] p-2'>
                                        <TbZoomMoney className='text-[#D64BC2]' />
                                    </div>
                                    <HiDotsVertical />
                                </div>
                                <span className='text-[#9FA8C7] text-[14px]'>Current Balance</span>
                                <div className='flex flex-col'>
                                    <div className='flex justify-between items-center'>
                                        <p className='text-[#25282C] text-[25px] font-[600]'>₹{currentBalance.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
                                        <div className={`${totalEarning >= 0 ? 'bg-[#FFEEEE]' : 'bg-[#E5FFF1]'} w-[75px] rounded-[40px] flex justify-center items-center py-1`}>
                                            <span className={`${totalEarning >= 0 ? 'text-[#F83C3F]' : 'text-[#00AE4E]'} text-[12px]`}>
                                                {openingBalance > 0 && totalEarning !== 0
                                                    ? `${(totalEarning >= 0 ? '+' : '')}${((totalEarning / openingBalance) * 100).toFixed(2)}%`
                                                    : '0%'}
                                            </span>
                                        </div>
                                    </div>
                                    {/* Show invested and return percent below Current Balance */}
                                    <div className="flex flex-wrap mt-1 text-xs gap-1 items-center">
                                        <span className="text-[#5d669d]">Invested:</span>
                                        <span className="text-[#377983] font-semibold">
                                            ₹{partyInvested.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                        </span>
                                        <span className="text-[#b1b5c4] mx-1">|</span>
                                        <span className="text-[#6CBC41]">Return:</span>
                                        <span className="text-[#3da900] font-semibold">{partyReturnPercent}</span>
                                    </div>
                                </div>
                            </div>
                            <div className='lg:w-[32%] md:w-[48%] w-[100%] bg-white border-[1px] border-[#DDDDDD] rounded-[14px] p-3'>
                                <div className='w-full flex justify-between items-center mb-3'>
                                    <div className='bg-[#DDFFE7] rounded-[50%] p-2'>
                                        <GiWallet className='text-[#27C153]' />
                                    </div>
                                    <HiDotsVertical />
                                </div>
                                <span className='text-[#9FA8C7] text-[14px]'>Total Earning</span>
                                <div className='flex justify-between items-center'>
                                    <p className='text-[#25282C] text-[25px] font-[600]'>₹{totalEarning.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
                                    <div className={`${totalEarning >= 0 ? 'bg-[#E5FFF1]' : 'bg-[#FFEEEE]'} w-[75px] rounded-[40px] flex justify-center items-center py-1`}>
                                        <span className={`${totalEarning >= 0 ? 'text-[#00AE4E]' : 'text-[#F83C3F]'} text-[12px]`}>
                                            {openingBalance > 0 && totalEarning !== 0
                                                ? `${(totalEarning >= 0 ? '+' : '')}${((totalEarning / openingBalance) * 100).toFixed(2)}%`
                                                : '0%'}
                                        </span>
                                    </div>
                                </div>
                            </div>
                            {useBaseCalculation && (
                                <div className="w-full bg-gradient-to-r from-[#FFF8E6] to-[#FEFFF7] mt-4 rounded-xl p-4 border-l-8 border-[#F8C615] shadow-lg flex flex-col gap-2">
                                    {interestSummary && (
                                        <div className="flex items-center gap-4">
                                            <div className="flex items-center gap-2">
                                                <span className="inline-block bg-[#FFFBE5] text-[#C49B11] font-semibold px-3 py-1 rounded-full text-xs shadow border border-[#F8C615]">
                                                    {interestSummary.type === 'simple' ? 'Simple Interest' : 'Compound Interest'}
                                                </span>
                                                <span className="inline-block text-[#7C7128] text-xs font-medium ml-2">
                                                    <span className="bg-[#FEF3C7] px-2 py-0.5 rounded border border-[#F8C615] font-semibold mr-1">
                                                        Rate: {interestSummary.rate}%
                                                    </span>
                                                </span>
                                            </div>
                                            <div className="flex items-center ml-auto">
                                                <span className="text-[#888155] text-xs mr-2">Calculated Interest:</span>
                                                <span className="text-[#FFAA2D] text-2xl font-extrabold drop-shadow">
                                                    ₹{Number(interestSummary.calculated).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                                </span>
                                            </div>
                                        </div>
                                    )}
                                    <div className="bg-white rounded-md p-3 mt-2 flex flex-col sm:flex-row items-baseline gap-2 border border-dashed border-[#F8C615]/30">
                                        <span className="text-[#A8975D] font-medium text-xs sm:text-sm mb-1">Total Earning With Base</span>
                                        <span className="flex items-center gap-2">
                                            <span className="text-[#575A36] font-semibold text-lg">
                                                ₹{openingBalance.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                            </span>
                                            {interestSummary && typeof interestSummary.calculated !== 'undefined' && !isNaN(Number(interestSummary.calculated)) && (
                                                <>
                                                    <span className="text-lg text-[#F8A600] font-extrabold">-</span>
                                                    <span className="text-[#FBC02D] font-semibold text-lg bg-[#FFF8E1] px-2 py-0.5 rounded">{`₹${Number(interestSummary.calculated).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}</span>
                                                    <span className="text-lg text-[#F8A600] font-extrabold">=</span>
                                                    <span className="font-bold text-lg px-3 py-1 rounded-full bg-gradient-to-r from-[#FFFBE5] to-[#FEFFF7] border border-[#F8C615] text-[#F8A600] shadow-inner">
                                                        ₹{(openingBalance - Number(interestSummary.calculated)).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                                    </span>
                                                </>
                                            )}
                                        </span>
                                    </div>
                                    <div className="mt-2 flex gap-2 items-center">
                                        <span className="w-2 h-2 rounded-full bg-[#F8C615] animate-pulse shadow-lg"></span>
                                        <span className="text-[12px] sm:text-xs text-[#C49B11] font-semibold tracking-wide">
                                            Make smarter investment decisions by tracking your base interest separately!
                                        </span>
                                    </div>
                                </div>
                            )}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4 w-full">
                                {/* Growth Card */}
                                <div className="bg-white rounded-xl p-5 shadow-sm flex flex-col gap-2 min-h-[285px]">
                                    <div className="flex flex-col gap-2 sm:flex-row sm:justify-between sm:items-center mb-2">
                                        <div className="min-w-0">
                                            <div className="text-[#7B849B] font-medium text-xs mb-0.5 truncate">Growth</div>
                                            <span className="text-[#b1b5c4] text-xs font-regular truncate block">Working Balance</span>
                                        </div>
                                        <div className="flex flex-wrap gap-2 min-w-0">
                                            <select
                                                className="rounded-[6px] py-1 px-2 text-xs bg-[#F7F9FB] text-[#B1B5C4] outline-none border border-[#E6E9F0] max-w-[120px] truncate"
                                                value={growthCategory}
                                                onChange={e => setGrowthCategory(e.target.value)}>
                                                {uniqueCategories.map((cat, i) =>
                                                    <option key={cat || i} value={cat}>{cat}</option>
                                                )}
                                            </select>
                                            <select
                                                className="rounded-[6px] py-1 px-2 text-xs bg-[#F7F9FB] text-[#B1B5C4] outline-none border border-[#E6E9F0] max-w-[120px] truncate"
                                                value={growthPeriod}
                                                onChange={e => setGrowthPeriod(e.target.value)}>
                                                {PERIOD_OPTIONS.map((p) =>
                                                    <option key={p.key} value={p.key}>{p.label}</option>
                                                )}
                                            </select>
                                        </div>
                                        <div className="min-w-0 mt-2 sm:mt-0 sm:ml-2 flex-shrink-0">
                                            <span className="text-[#B1B5C4] text-xs whitespace-nowrap">Currency</span>
                                            <span className="ml-1 text-[#222] text-xs whitespace-nowrap">Rupees</span>
                                        </div>
                                    </div>
                                    <div className="flex flex-col justify-between gap-2 lg:flex-row">
                                        <div className="flex flex-col justify-between h-[180px] min-w-0">
                                            <div className="pt-1 pb-2 flex flex-col min-w-0">
                                                <div className="text-[#24272B] font-bold text-base sm:text-xl mt-3 truncate">₹{currentBalance.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                                                <div className="text-xs text-[#B1B5C4] truncate">
                                                    {growthChartData.labels.length > 0
                                                        ? growthChartData.labels[growthChartData.labels.length - 1]
                                                        : new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                                                </div>
                                            </div>
                                            <div className="w-full h-[110px] relative min-w-0 overflow-x-auto">
                                                {growthChartData.labels.length > 0 && growthChartData.datasets[0].data.length > 0 ? (
                                                    <>
                                                        <Line data={growthChartData} options={growthOptions} height={110} />
                                                        {growthChartData.datasets[0].data.length > 0 && (
                                                            <div className="absolute left-1/2 -translate-x-1/2 bottom-[30px] z-10 flex flex-col items-center pointer-events-none max-w-full">
                                                                <span className="bg-white shadow px-2 py-1 rounded text-xs font-semibold text-[#319F68] border border-[#e6e9f0] whitespace-nowrap">
                                                                    ₹{growthChartData.datasets[0].data[growthChartData.datasets[0].data.length - 1].toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                                                                </span>
                                                                <div className="w-0 h-0 border-l-[6px] border-l-transparent border-r-[6px] border-r-transparent border-t-[10px] border-t-[#E6E9F0]"></div>
                                                            </div>
                                                        )}
                                                    </>
                                                ) : (
                                                    <div className="flex items-center justify-center h-full text-[#B1B5C4] text-sm">
                                                        No transaction data available
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                {/* Pie & Data Card */}
                                <div className="bg-white rounded-xl p-5 shadow-sm flex flex-col gap-3 min-h-[285px]">
                                    <div className="flex flex-row gap-2 xs:flex-row xs:justify-between xs:items-center">
                                        <div className="font-semibold text-[#222] text-[15px] min-w-0 truncate">Asset Categories</div>
                                        <div className="flex flex-wrap gap-2 min-w-0">
                                            <select
                                                className="rounded-[6px] py-1 px-2 text-xs bg-[#F7F9FB] text-[#B1B5C4] outline-none border border-[#E6E9F0] max-w-[120px] truncate"
                                                value={pieCategory}
                                                onChange={e => setPieCategory(e.target.value)}>
                                                {uniqueCategories.map((cat, i) =>
                                                    <option key={cat || i} value={cat}>{cat}</option>
                                                )}
                                            </select>
                                            <select
                                                className="rounded-[6px] py-1 px-2 text-xs bg-[#F7F9FB] text-[#B1B5C4] outline-none border border-[#E6E9F0] max-w-[120px] truncate"
                                                value={piePeriod}
                                                onChange={e => setPiePeriod(e.target.value)}>
                                                {PERIOD_OPTIONS.map((p) =>
                                                    <option key={p.key} value={p.key}>{p.label}</option>
                                                )}
                                            </select>
                                        </div>
                                    </div>
                                    <div className="flex flex-col gap-2 xs:flex-row xs:gap-3 w-full">
                                        {pieData.labels.length > 0 && pieData.datasets[0].data.length > 0 ? (
                                            <>
                                                <div className="flex-shrink-0 mx-auto xs:mx-0 w-[80px] h-[80px] sm:w-[105px] sm:h-[105px]">
                                                    <Pie data={pieData} options={pieOptions} />
                                                </div>
                                                <div className="flex flex-col justify-center gap-2 text-xs sm:text-sm w-full min-w-0">
                                                    {pieCategoryDetails.map((detail, index) => (
                                                        <div key={index} className="flex flex-col xs:flex-row xs:items-center gap-1 flex-wrap xs:gap-2 min-w-0">
                                                            <div className="flex items-center gap-1">
                                                                <span className="inline-block w-3 h-3 rounded-full flex-shrink-0" style={{ background: detail.color }}></span>
                                                                <span className="font-semibold whitespace-nowrap">{detail.percentage.toFixed(1)}%</span>
                                                                <span className="text-[#B1B5C4] truncate">{detail.name}</span>
                                                                <span className={`${detail.amount >= 0 ? 'text-[#319F68]' : 'text-[#D44334]'} font-bold ml-1 xs:ml-2 whitespace-nowrap truncate`}>
                                                                    ₹{detail.amount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                                                </span>
                                                            </div>
                                                            {detail.stockBreakdown && detail.stockBreakdown.length > 0 && (
                                                                <div className="text-[10px] text-[#69813C] pl-5">
                                                                    {detail.stockBreakdown
                                                                        .filter((s: any) => !!s.stock_name)
                                                                        .map((s: any, idx: number) =>
                                                                            <div key={idx} className="truncate break-all">
                                                                                • {s.stock_name}
                                                                                {typeof s.unit === 'number' || (typeof s.unit === 'string' && s.unit !== undefined && String(s.unit) !== '') ? (<span> ({s.unit})</span>) : ''}
                                                                                {typeof s.total_amount === 'number' && (
                                                                                    <span> ₹{s.total_amount.toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}</span>
                                                                                )}
                                                                                {typeof s.indirect_charges === 'number' && s.indirect_charges > 0 && (
                                                                                    <span> +₹{s.indirect_charges.toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} IC</span>
                                                                                )}
                                                                                {typeof s.perUnitCharge === 'number' && s.perUnitCharge !== 0 && (
                                                                                    <span> | PerUnit: {s.perUnitCharge}</span>
                                                                                )}
                                                                            </div>
                                                                        )}
                                                                </div>
                                                            )}
                                                        </div>
                                                    ))}
                                                </div>
                                            </>
                                        ) : (
                                            <div className="flex items-center justify-center w-full h-[150px] text-[#B1B5C4] text-sm">
                                                No stock categories available
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    {/* Sidebar */}
                    <div className={`bg-[#fff] h-[100vh] sticky top-0 bottom-0 transition-all duration-300 ${sidebarCollapsed ? "lg:w-[64px] w-[64px]" : "lg:w-[25%] w-[100%]"}`}>
                        <div
                            className={`
                                sticky
                                top-6
                                self-start
                            `}
                            style={{ zIndex: 10 }}
                        >
                            {/* Collapse/Expand Toggle Button */}
                            <div className="flex items-center justify-end m-2">
                                <button
                                    className="p-2 rounded hover:bg-gray-100 transition-all duration-200"
                                    onClick={() => setSidebarCollapsed((prev: boolean) => !prev)}
                                    aria-label={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
                                    type="button"
                                >
                                    {sidebarCollapsed
                                        ? <svg width="20" height="20" fill="none" viewBox="0 0 24 24"><path d="M10 6l6 6-6 6" stroke="#3F4765" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>
                                        : <svg width="20" height="20" fill="none" viewBox="0 0 24 24"><path d="M14 6l-6 6 6 6" stroke="#3F4765" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>
                                    }
                                </button>
                            </div>
                            <div className={`bg-[#F1FFF6] m-3 mt-6 p-3 transition-all duration-300 ${sidebarCollapsed ? "px-2 py-2" : ""}`}>
                                <p className={sidebarCollapsed ? "text-[0px] h-0 overflow-hidden" : ""}>
                                    Calculation to be done with Base <br />
                                </p>
                                <div className={`flex gap-4 rounded-[10px] p-2 ${sidebarCollapsed ? "flex-col items-center gap-0 p-0" : ""}`}>
                                    <label className={`flex items-center gap-2 cursor-pointer ${sidebarCollapsed ? "flex-col gap-0" : ""}`}>
                                        <input
                                            type="radio"
                                            name="basecalculation"
                                            value="yes"
                                            checked={useBaseCalculation}
                                            onChange={() => setUseBaseCalculation(true)}
                                        />
                                        {!sidebarCollapsed && "Yes"}
                                    </label>
                                    <label className={`flex items-center gap-2 cursor-pointer ${sidebarCollapsed ? "flex-col gap-0" : ""}`}>
                                        <input
                                            type="radio"
                                            name="basecalculation"
                                            value="no"
                                            checked={!useBaseCalculation}
                                            onChange={() => setUseBaseCalculation(false)}
                                        />
                                        {!sidebarCollapsed && "No"}
                                    </label>
                                </div>
                            </div>
                            {!sidebarCollapsed && (
                                <>
                                    <hr />
                                    <div className='flex justify-between mx-3 mt-4'>
                                        <span className='text-[#3F4765] text-[18px]'>Parties Name</span>
                                        <select name="" id="" className='text-[#969CCB] text-[14px]'>
                                            <option value="All" >All</option>
                                        </select>
                                    </div>
                                </>
                            )}
                            <div className="overflow-x-auto w-full">
                                <table className={`mr-3 ml-3 border-separate border-spacing-y-2 transition-all duration-300 ${sidebarCollapsed ? "text-center" : ""}`}>
                                    <tbody>
                                        {allParties.map((party: Party) => (
                                            <tr key={party.id} className="bg-white">
                                                <td className={`text-[12px] text-[#3F4765] whitespace-nowrap pr-2`}>
                                                    <Link to={`/party/${party.id}`}>
                                                        <span className="text-[10px] text-white bg-[#FF8933] inline-flex items-center justify-center h-[15px] w-[15px] rounded-full mr-1">
                                                            {party.name && typeof party.name === 'string' ? party.name.charAt(0) : ''}
                                                        </span>
                                                        {!sidebarCollapsed && party.name}
                                                    </Link>
                                                </td>
                                                {!sidebarCollapsed && (
                                                    <>
                                                        <td className="text-[12px] text-[#3F4765] whitespace-nowrap ">
                                                            ₹{party.amount}
                                                        </td>
                                                        <td className="text-[12px] text-[#3F4765] whitespace-nowrap">
                                                            {party.percentage}%
                                                        </td>
                                                    </>
                                                )}
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default SinglePartyPage;
